import { PLATFORM_NAME, TAGLINE } from '@auctisync/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@auctisync/ui';
import { useToast } from '../../components/Toast';
import { PlanPanel } from '../../components/PlanPanel';

type Surface = {
  key: string;
  name: string;
  data: Record<string, string>;
  bg: string;
  label: string;
  style?: React.CSSProperties;
};

const surfaces: Surface[] = [
  {
    key: 'marketing',
    name: 'Marketing',
    data: { 'data-theme': 'marketing' },
    bg: 'bg-white',
    label: 'auctisync.com · B2B',
  },
  {
    key: 'admin',
    name: 'Tenant Admin',
    data: { 'data-theme': 'admin' },
    bg: 'bg-gray-50',
    label: 'app.auctisync.com · staff tool',
  },
  {
    key: 'super-admin',
    name: 'Super Admin',
    data: { 'data-theme': 'super-admin' },
    bg: 'bg-violet-50',
    label: 'admin.auctisync.com · platform ops',
  },
  {
    key: 'marketplace',
    name: 'Marketplace',
    data: { 'data-theme': 'marketplace' },
    bg: 'bg-white',
    label: '[B2C TBD].com · Phase 2',
  },
];

type TenantPreset = {
  key: string;
  preset: 'modern' | 'classic' | 'editorial';
  label: string;
  exampleBrand: {
    name: string;
    primary: string;
    accent: string;
    surface?: string;
  };
};

const tenantExamples: TenantPreset[] = [
  {
    key: 'dta-modern',
    preset: 'modern',
    label: 'DTA · Modern preset · brand red+black',
    exampleBrand: {
      name: 'Discount Tool Auctions',
      primary: '#b91c1c',
      accent: '#0a0a0a',
    },
  },
  {
    key: 'heritage-classic',
    preset: 'classic',
    label: 'Heritage Fine Arts · Classic preset · navy+gold',
    exampleBrand: {
      name: 'Heritage Fine Arts',
      primary: '#0c1f3d',
      accent: '#c8a44a',
      surface: '#fefcf5',
    },
  },
  {
    key: 'noir-editorial',
    preset: 'editorial',
    label: 'Maison Noir · Editorial preset · magenta+stone',
    exampleBrand: {
      name: 'Maison Noir',
      primary: '#881337',
      accent: '#57534e',
      surface: '#fafaf9',
    },
  },
];

/**
 * Dev-only design-system showcase. Preserved from the pre-routing App.tsx
 * so the 4 surfaces + bidder preset framework stay visible for review.
 * Accessible at `/design`.
 */
function ToastDemo() {
  const toast = useToast();
  return (
    <div className="flex flex-wrap gap-3">
      <Button size="sm" onClick={() => toast.success('Saved.')}>
        Show success
      </Button>
      <Button size="sm" variant="outline" onClick={() => toast.info('Heads up — draft autosaved.')}>
        Show info
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => toast.error('Could not save. Check your connection and try again.')}
      >
        Show error
      </Button>
    </div>
  );
}

export function DesignShowcase() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-8 inline-block rounded-full bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wider text-blue-300 ring-1 ring-white/10">
          DESIGN SYSTEM · DAY 4 SCAFFOLD
        </div>
        <h1 className="mb-3 text-5xl font-bold tracking-tight">{PLATFORM_NAME}</h1>
        <p className="mb-12 text-xl text-slate-300 italic">{TAGLINE}</p>

        <section className="mb-12">
          <h2 className="mb-4 text-sm font-semibold tracking-wider text-slate-400 uppercase">
            Four AuctiSync surfaces
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {surfaces.map((surface) => (
              <div key={surface.key} {...surface.data} className={`${surface.bg} rounded-lg p-4`}>
                <div className="mb-3">
                  <div
                    className="text-lg font-bold"
                    style={{ color: 'var(--text)', fontFamily: 'var(--font-heading)' }}
                  >
                    {surface.name}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {surface.label}
                  </div>
                </div>
                <div className="space-y-2">
                  <Button size="sm">Primary action</Button>
                  <div className="flex gap-2">
                    <Badge variant="live">● LIVE</Badge>
                    <Badge variant="success">Active</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold tracking-wider text-slate-400 uppercase">
            Bidder framework — 3 preset structures × tenant brand colors
          </h2>
          <p className="mb-6 text-sm text-slate-400">
            Same <code className="rounded bg-white/10 px-1">bidder</code> theme — different presets
            control structure/typography/shape — tenant brand colors fill the slots. Same lot
            rendered 3 ways.
          </p>
          <div className="grid gap-4 lg:grid-cols-3">
            {tenantExamples.map((t) => (
              <div
                key={t.key}
                data-theme="bidder"
                data-preset={t.preset}
                style={
                  {
                    '--brand-primary': t.exampleBrand.primary,
                    '--brand-accent': t.exampleBrand.accent,
                    '--brand-surface': t.exampleBrand.surface ?? '#ffffff',
                    '--surface': t.exampleBrand.surface ?? '#ffffff',
                  } as React.CSSProperties
                }
                className="rounded-lg p-1"
              >
                <Card>
                  <CardHeader>
                    <div
                      className="text-xs"
                      style={{
                        color: 'var(--text-muted)',
                        textTransform: 'var(--label-transform)' as never,
                        letterSpacing: 'var(--label-tracking)',
                        fontWeight: 'var(--label-weight)' as never,
                        fontFamily: 'var(--label-font)',
                        fontStyle: 'var(--label-style)' as never,
                      }}
                    >
                      {t.label}
                    </div>
                    <CardTitle
                      style={{
                        fontFamily: 'var(--font-heading-active)',
                        fontWeight: t.preset === 'editorial' ? 400 : 700,
                      }}
                    >
                      1946 Stanley No. 4 bench plane
                    </CardTitle>
                    <CardDescription>{t.exampleBrand.name} · Est. $100 – 200</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 flex items-baseline justify-between">
                      <span
                        className="text-lg"
                        style={{
                          fontFamily: 'var(--price-font)',
                          fontWeight: 'var(--price-weight)' as never,
                          fontStyle: 'var(--price-style)' as never,
                          color: 'var(--text)',
                        }}
                      >
                        $127.50
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--countdown-font)',
                          fontWeight: 'var(--countdown-weight)' as never,
                          fontStyle: 'var(--countdown-style)' as never,
                          color: 'var(--semantic-live)',
                        }}
                      >
                        12m 14s
                      </span>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-2">
                      <Badge variant="live">● LIVE</Badge>
                      <Badge variant="outline">94 watching</Badge>
                    </div>
                    <Button size="sm" className="w-full">
                      Place bid · $130
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="mb-2 text-sm font-semibold tracking-wider text-slate-400 uppercase">
            Toast — app-wide save/action feedback
          </h2>
          <p className="mb-6 text-sm text-slate-400">
            The shared <code className="rounded bg-white/10 px-1">useToast()</code> primitive.
            Renders in a fixed top-right viewport (visible regardless of scroll), auto-dismisses,
            and inherits theme + tenant brand tokens. Success/info are transient; errors dwell
            longer.
          </p>
          <ToastDemo />
        </section>

        <section className="mt-12">
          <h2 className="mb-2 text-sm font-semibold tracking-wider text-slate-400 uppercase">
            Plan panel — tier entitlements, and the note naming each plan
          </h2>
          <p className="mb-6 text-sm text-slate-400">
            The live surface is <code className="rounded bg-white/10 px-1">/admin/billing</code>,
            which needs a signed-in owner — shown here across three tiers so the wording can be
            compared side by side. A trial sees everything switched on and each row captioned with
            the plan that carries it; a paying tenant sees what they'd upgrade to; a comp sees
            neither, because captioning a free account with plan names reads as a sales pitch.
          </p>
          {/* data-theme="admin" is load-bearing, not decoration. PlanPanel reads
              --text / --text-muted / --admin-hairline, which are defined by the
              surface theme rather than globally. This showcase page carries no
              theme, so without this wrapper the panel inherits white text on a
              white card and renders invisibly — which is exactly what it did on
              first mount here. Rendering it inside its real theme also means
              what is shown below is what an owner actually sees. */}
          <div
            data-theme="admin"
            className="grid gap-6 rounded-lg p-4 lg:grid-cols-3"
            style={{ background: 'var(--surface, #f9fafb)' }}
          >
            <div>
              <p
                className="mb-2 text-xs tracking-wider uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                tier: trial
              </p>
              <PlanPanel tier="trial" />
            </div>
            <div>
              <p
                className="mb-2 text-xs tracking-wider uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                tier: starter
              </p>
              <PlanPanel tier="starter" />
            </div>
            <div>
              <p
                className="mb-2 text-xs tracking-wider uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                tier: complimentary
              </p>
              <PlanPanel tier="complimentary" />
            </div>
          </div>
        </section>

        <div className="mt-12 border-t border-white/10 pt-6 text-center text-xs text-slate-500">
          <p>
            shadcn-style components · CSS-variable theming · 5 AuctiSync surfaces + tenant preset
            framework · 7-tier customization ladder
          </p>
        </div>
      </div>
    </main>
  );
}
